import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { 
  Search, Eye, Clock, CheckCircle, Truck, XCircle, Filter, ShoppingCart
} from 'lucide-react';
import { AdminLayout } from '../../components/AdminLayout';
import { useAdminTheme } from '../../contexts/AdminThemeContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL ? `${BACKEND_URL.replace(/\/$/, '')}/api` : '/api';

const statusConfig = {
  pending: { label: 'Pendente', color: 'bg-yellow-500', icon: Clock },
  confirmed: { label: 'Confirmado', color: 'bg-blue-500', icon: CheckCircle },
  shipped: { label: 'Enviado', color: 'bg-purple-500', icon: Truck },
  delivered: { label: 'Entregue', color: 'bg-green-500', icon: CheckCircle },
  cancelled: { label: 'Cancelado', color: 'bg-red-500', icon: XCircle }
};

const paymentStatusConfig = {
  pending: { label: 'Aguardando', color: 'bg-yellow-500' },
  paid: { label: 'Pago', color: 'bg-green-500' },
  failed: { label: 'Falhou', color: 'bg-red-500' }
};

export const AdminOrders = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);

  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = (params.get('q') || '').trim();
    if (q) setSearchTerm(q);
  }, [location.search]);

  useEffect(() => {
    fetchOrders();
    const interval = setInterval(() => {
      fetchOrders();
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Error fetching orders:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        navigate('/admin/login');
      }
    } finally {
      setLoading(false);
    }
  };

  const updateOrderStatus = async (orderId, newStatus) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status`, 
        { status: newStatus },
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true
        }
      );
      fetchOrders();
    } catch (error) {
      console.error('Error updating order:', error);
      alert('Erro ao atualizar pedido');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredOrders = orders.filter(order => {
    const q = searchTerm.toLowerCase();
    const matchesSearch = order.id.toLowerCase().includes(q) ||
      (order.order_code && order.order_code.toLowerCase().includes(q)) ||
      (order.user_email && order.user_email.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const { darkMode, theme } = useAdminTheme();

  return (
    <AdminLayout title="Pedidos" subtitle="Gerencie os pedidos da loja">
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Total</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{orders.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-amber-100 dark:bg-amber-900/30 rounded-xl flex items-center justify-center">
              <Clock className="h-6 w-6 text-amber-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Pendentes</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{orders.filter(o => o.status === 'pending').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-purple-100 dark:bg-purple-900/30 rounded-xl flex items-center justify-center">
              <Truck className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Enviados</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{orders.filter(o => o.status === 'shipped').length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className={`${theme.card} border rounded-2xl`}>
          <CardContent className="p-6 flex items-center space-x-4">
            <div className="w-12 h-12 bg-emerald-100 dark:bg-emerald-900/30 rounded-xl flex items-center justify-center">
              <CheckCircle className="h-6 w-6 text-emerald-600" />
            </div>
            <div>
              <p className={`text-sm ${theme.textMuted}`}>Entregues</p>
              <p className={`text-2xl font-bold ${theme.text}`}>{orders.filter(o => o.status === 'delivered').length}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div data-tour="orders-filters" className="flex flex-wrap gap-4 mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${theme.textMuted}`} />
          <Input
            placeholder="Buscar por ID ou email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`pl-10 ${theme.input} rounded-xl`}
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className={`w-48 ${theme.input} rounded-xl`}>
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className={theme.dropdown}>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="pending">Pendentes</SelectItem>
            <SelectItem value="confirmed">Confirmados</SelectItem>
            <SelectItem value="shipped">Enviados</SelectItem>
            <SelectItem value="delivered">Entregues</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Orders Table */}
      <Card data-tour="orders-table" className={`${theme.card} border rounded-2xl`}>
        <CardContent className="p-0">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className={theme.tableRow}>
                  <TableHead className={theme.tableHead}>Pedido</TableHead>
                  <TableHead className={theme.tableHead}>Cliente</TableHead>
                  <TableHead className={theme.tableHead}>Data</TableHead>
                  <TableHead className={theme.tableHead}>Total</TableHead>
                  <TableHead className={theme.tableHead}>Pagamento</TableHead>
                  <TableHead className={theme.tableHead}>Status</TableHead>
                  <TableHead className={theme.tableHead}>Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map(order => {
                  const StatusIcon = statusConfig[order.status]?.icon || Clock;
                  return (
                    <TableRow key={order.id} className={theme.tableRow}>
                      <TableCell className={`${theme.text} font-mono`}>
                        <div className="leading-tight">
                          <div>#{order.id.slice(0, 8)}</div>
                          {order.order_code && (
                            <div className={`${theme.textMuted} text-xs`}>{order.order_code}</div>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className={theme.text}>{order.user_name || 'N/A'}</p>
                          <p className={`${theme.textMuted} text-sm`}>{order.user_email || 'N/A'}</p>
                        </div>
                      </TableCell>
                      <TableCell className={theme.textSecondary}>{formatDate(order.created_at)}</TableCell>
                      <TableCell className="text-blue-600 font-semibold">{formatCurrency(order.total)}</TableCell>
                      <TableCell>
                        <Badge className={`${paymentStatusConfig[order.payment_status]?.color} text-white`}>
                          {paymentStatusConfig[order.payment_status]?.label}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Select value={order.status} onValueChange={(v) => updateOrderStatus(order.id, v)}>
                          <SelectTrigger className={`w-36 ${statusConfig[order.status]?.color} bg-opacity-20 border-0 text-white`}>
                            <StatusIcon className="h-4 w-4 mr-2" />
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className={theme.dropdown}>
                            <SelectItem value="pending">Pendente</SelectItem>
                            <SelectItem value="confirmed">Confirmado</SelectItem>
                            <SelectItem value="shipped">Enviado</SelectItem>
                            <SelectItem value="delivered">Entregue</SelectItem>
                            <SelectItem value="cancelled">Cancelado</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => { setSelectedOrder(order); setIsDetailOpen(true); }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Order Detail Dialog */}
      <Dialog open={isDetailOpen} onOpenChange={setIsDetailOpen}>
        <DialogContent className={`${theme.card} border ${theme.text} max-w-2xl`}>
          <DialogHeader>
            <DialogTitle className={theme.text}>Detalhes do Pedido #{selectedOrder?.id.slice(0, 8)}</DialogTitle>
            <DialogDescription className={theme.textMuted}>
              {selectedOrder && formatDate(selectedOrder.created_at)}
            </DialogDescription>
          </DialogHeader>
          
          {selectedOrder && (
            <div className="space-y-6">
              {/* Customer Info */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'} p-4 rounded-xl`}>
                <h4 className={`text-sm font-medium ${theme.textMuted} mb-2`}>Cliente</h4>
                <p className={theme.text}>{selectedOrder.user_name}</p>
                <p className={theme.textSecondary}>{selectedOrder.user_email}</p>
              </div>

              {/* Address */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'} p-4 rounded-xl`}>
                <h4 className={`text-sm font-medium ${theme.textMuted} mb-2`}>Endereço de Entrega</h4>
                <p className={theme.text}>
                  {selectedOrder.address?.street}, {selectedOrder.address?.number}
                  {selectedOrder.address?.complement && ` - ${selectedOrder.address.complement}`}
                </p>
                <p className={theme.textSecondary}>
                  {selectedOrder.address?.neighborhood} - {selectedOrder.address?.city}/{selectedOrder.address?.state}
                </p>
                <p className={theme.textSecondary}>CEP: {selectedOrder.address?.zipcode}</p>
              </div>

              {/* Items */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'} p-4 rounded-xl`}>
                <h4 className={`text-sm font-medium ${theme.textMuted} mb-3`}>Itens do Pedido</h4>
                <div className="space-y-3">
                  {selectedOrder.items?.map((item, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 ${darkMode ? 'bg-slate-600' : 'bg-gray-200'} rounded-lg overflow-hidden`}>
                          {item.image && <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />}
                        </div>
                        <div>
                          <p className={theme.text}>{item.product_name}</p>
                          <p className={`${theme.textMuted} text-sm`}>Qtd: {item.quantity}</p>
                        </div>
                      </div>
                      <p className={`${theme.text} font-medium`}>{formatCurrency(item.price * item.quantity)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Totals */}
              <div className={`${darkMode ? 'bg-slate-700/50' : 'bg-gray-50'} p-4 rounded-xl`}>
                <div className={`flex justify-between ${theme.textSecondary} mb-2`}>
                  <span>Subtotal</span>
                  <span>{formatCurrency(selectedOrder.total - selectedOrder.shipping_cost)}</span>
                </div>
                <div className={`flex justify-between ${theme.textSecondary} mb-2`}>
                  <span>Frete</span>
                  <span>{formatCurrency(selectedOrder.shipping_cost)}</span>
                </div>
                <div className={`flex justify-between ${theme.text} font-bold text-lg pt-2 border-t ${darkMode ? 'border-slate-600' : 'border-gray-200'}`}>
                  <span>Total</span>
                  <span className="text-blue-600">{formatCurrency(selectedOrder.total)}</span>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};
